import type { QueryOptions } from "@tanstack/react-query"
import type { ExactPartial } from "@wagmi/core/internal"
import { Evaluate, getAddress } from "viem"
import {
  type GetAccountInfoErrorType,
  type GetAccountInfoParameters,
  type GetAccountInfoReturnType,
  getAccountInfo,
} from "../actions/getAccountInfo"
import type { ExtendedConfig, ScopeKeyParameter } from "../types"
import { filterQueryOptions } from "../utils"

export type GetAccountInfoOptions<
  config extends ExtendedConfig = ExtendedConfig,
> = Evaluate<ExactPartial<GetAccountInfoParameters<config>> & ScopeKeyParameter>

export function getAccountInfoQueryKey<
  config extends ExtendedConfig = ExtendedConfig,
>(options: GetAccountInfoOptions<config> = {}) {
  const { account, vault, evcAddress, accountLensAddress, ...parameters } =
    options

  return [
    "evcAccountInfo",
    filterQueryOptions({
      ...parameters,
      account: account && getAddress(account),
      vault: vault && getAddress(vault),
      evcAddress: evcAddress && getAddress(evcAddress),
      accountLensAddress: accountLensAddress && getAddress(accountLensAddress),
    }),
  ] as const
}

export function getAccountInfoQueryOptions<config extends ExtendedConfig>(
  config: config,
  options: GetAccountInfoOptions<config>,
) {
  return {
    gcTime: 0,
    async queryFn({ queryKey }) {
      const { scopeKey: _s, ...parameters } = queryKey[1]
      return getAccountInfo<config>(config, parameters)
    },
    queryKey: getAccountInfoQueryKey<config>(options),
  } satisfies QueryOptions<
    GetAccountInfoQueryFnData,
    GetAccountInfoErrorType,
    GetAccountInfoData,
    GetAccountInfoQueryKey<config>
  >
}

export type GetAccountInfoQueryFnData = GetAccountInfoReturnType

export type GetAccountInfoData = GetAccountInfoQueryFnData

export type GetAccountInfoQueryKey<
  config extends ExtendedConfig = ExtendedConfig,
> = ReturnType<typeof getAccountInfoQueryKey<config>>
